import { useState, type ImgHTMLAttributes, type ReactNode } from 'react';

import { safeHttpUrl } from '@/lib/safeHttpUrl';

export interface SiteImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'referrerPolicy' | 'loading' | 'decoding'> {
  /** A URL read off a third-party site: drawn only when it is http(s). */
  src: string | null | undefined;
  /** Shown instead when the URL is missing, unsafe, or fails to load. */
  fallback?: ReactNode;
}

/**
 * An `<img>` for a site-derived URL. The request never leaks this page's address (`no-referrer`), is
 * deferred until near the viewport and decoded off the main thread. Third-party images expire and get
 * blocked, so a failed load swaps to the fallback instead of leaving an empty box.
 */
export function SiteImage({ src, fallback = null, alt = '', ...rest }: SiteImageProps) {
  const safe = safeHttpUrl(src);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!safe || failedSrc === safe) return <>{fallback}</>;
  return (
    <img
      {...rest}
      src={safe}
      alt={alt}
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      onError={() => setFailedSrc(safe)}
    />
  );
}
